import { sharedGet } from './sharedGet';
import { useEffect, useState } from 'react';
import { createDocValueStore } from './docStore';
import { substepOutputsOf, type Job3D } from './threeDApi';

// WHAT THIS MANUAL'S SUB-STEPS HAVE MADE, by sub-step key — the meshes Phase 5 baked when
// somebody signed a movement off.
//
// WHAT THE LATER SUB-STEPS STAND ON: "insert the barrel nut into 102" is a movement onto the piece
// 1-2 made, and that piece is this mesh (see useDrawableParts, which hands it out as a part).
//
// AND AN EPOCH, because the phases do not unmount: a fetch on mount happens once per document and
// would never see a mesh baked afterwards. The sign-off bumps this.
const epochStore = createDocValueStore<number>(0);

export const bumpSubstepOutputs = (docId: string) =>
  epochStore.set(docId, epochStore.get(docId) + 1);

// THE MESH A SUB-STEP HAS MADE, if the one filed under its key is still OF what it makes.
//
// A bake is filed by slot key — step and letter — and a step analysed again re-letters its
// gestures: 1AC made "103 – third prepared screw" in August and makes "103 – first side rail
// with first barrel nut inserted" now, and the August screw went on answering for the rail.
// The label a bake was saved with names the piece it is ("<id> – <name>", in the language the
// page was in), so a bake counts only where that name is still one of the slot's two. A slot
// with no named output has nothing to check against and keeps what is filed under it.
export function bakedFor(
  slot: { key: string; output: { en: string; fr: string } | null },
  outputs: Map<string, Job3D>,
): Job3D | undefined {
  const job = outputs.get(slot.key);
  if (!job || !slot.output) return job;
  const norm = (s: string) => s.replace(/^\s*\d+\s*[–-]\s*/, '').trim().toLowerCase();
  const said = norm(String((job.params?.substep as { label?: string } | undefined)?.label ?? ''));
  return [slot.output.en, slot.output.fr].some((name) => name && norm(name) === said)
    ? job : undefined;
}

// THE LAST ANSWER, per document, kept for the page's life: a reader mounting again starts from
// what is known rather than from nothing. See useProductRuns, which does the same.
const lastOutputs = new Map<string, Map<string, Job3D>>();

// `outputs` is NULL UNTIL THE SERVER HAS ANSWERED. It started as an empty Map — the same value
// as "nothing baked" — so for a round trip every piece was marked as having no model, and a
// failed fetch said the same thing for good. `failed`: the last ask did not come back; what was
// known before it stays.
export function useSubstepOutputs(docId: string): { outputs: Map<string, Job3D> | null; failed: boolean } {
  const epoch = epochStore.useValue(docId);
  const [state, setState] = useState<{ docId: string; outputs: Map<string, Job3D> | null; failed: boolean }>(
    () => ({ docId, outputs: lastOutputs.get(docId) ?? null, failed: false }),
  );
  useEffect(() => {
    if (!docId) return;
    let alive = true;
    sharedGet<{ jobs: Job3D[] }>('/api/notice/3d/jobs', { params: { substep: docId } })
      .then(({ data }) => {
        const outputs = substepOutputsOf(data.jobs || [], docId);
        lastOutputs.set(docId, outputs);
        if (alive) setState({ docId, outputs, failed: false });
      })
      .catch(() => {
        if (alive) {
          setState((was) => ({
            docId,
            outputs: was.docId === docId ? was.outputs : lastOutputs.get(docId) ?? null,
            failed: true,
          }));
        }
      });
    return () => { alive = false; };
  }, [docId, epoch]);
  // Another document's answer is not this one's: until this one's lands, what is known of it.
  return state.docId === docId ? state : { outputs: lastOutputs.get(docId) ?? null, failed: false };
}
